import { Grid, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import AuthService from "api/Auth";
import styles from "assets/jss/public-styles";
import { RequestIndicator } from "components/CallServer";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { AccountConfirmationResult, AccountReconfirmationResult } from "models/ApiEnums";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { RoutePaths } from "routes/RouteConfig";

////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    submiting: boolean;
    confirmationResult: AccountConfirmationResult;
    newConfirmationResult: AccountReconfirmationResult;
    error: string | string[];
}

interface IProps {
}

interface IRouteProps {
    key: string;
}

type Props = IProps & RouteComponentProps<IRouteProps> & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

////////////////////////////////////////////////////////////////////////////////////////

class AccountConfirmation extends React.Component<Props, IState> {
    private authService = new AuthService();

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            submiting: true,
            confirmationResult: null,
            newConfirmationResult: null,
            error: null,
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Public.AccountConfirmation." + r);
    }

    ///
    private executeConfirmation() {

        this.authService.accountConfirmation(this.props.match.params.key)
            .then((response) => {
                this.setState({ confirmationResult: response, submiting: false });
            })
            .catch((error: any) => {
                this.setState({ error: error.message || error, submiting: false });
            });
    }

    ///
    private resendConfirmation() {

        this.setState({ submiting: true, confirmationResult: null, newConfirmationResult: null });

        this.authService.requestNewAccountConfirmation(this.props.match.params.key)
            .then((response) => {
                this.setState({ submiting: false, newConfirmationResult: response });
            })
            .catch((error: any) => {
                this.setState({ error: error.message || error, submiting: false });
            });
    }

    ///
    private renderError() {

        const message = this.state.error || this.res("ValidationError");

        return <MuiMessageBox
            variant="error"
            size="large"
            message={
                <Grid container justifyContent="center" alignContent="center">
                    <Grid item>
                        <Typography variant="h5" color="inherit">
                            <p>{message}</p>
                        </Typography>
                        <Typography variant="body2" color="inherit">
                            <p>{this.res("CallAdminMessage")}</p>
                        </Typography>
                    </Grid>
                </Grid>
            }
        />;
    }

    ///
    private renderNotFound() {

        return <MuiMessageBox
            variant="error"
            size="large"
            message={
                <Grid container justifyContent="center" alignContent="center">
                    <Grid item>
                        <Typography variant="h5" color="inherit">
                            <p>{this.res("AccountNotFoundText")}</p>
                        </Typography>
                        <Typography variant="body2" color="inherit">
                            <p>{this.res("CallAdminMessage")}</p>
                        </Typography>
                    </Grid>
                </Grid>
            }
        />;
    }

    ///
    private renderExpired() {

        return <MuiMessageBox
            variant="error"
            size="large"
            message={
                <Grid container justifyContent="center" alignContent="center">
                    <Grid item>
                        <Typography variant="h5" color="inherit">
                            <p>{this.res("ExpiredText")}</p>
                        </Typography>
                    </Grid>

                    <Grid item>
                        <MuiRegularButton
                            color="secondary"
                            size="large"
                            onClick={() => this.resendConfirmation()}
                        >
                            <span>{this.res("ButtonConfirmationText")}</span>
                        </MuiRegularButton>
                    </Grid>
                </Grid>
            }
        />;
    }

    ///
    private renderNewConfirmation() {
        if (this.state.confirmationResult == AccountConfirmationResult.NotFound)
            return this.renderNotFound();

        let message = "";
        switch (this.state.newConfirmationResult) {
            case AccountReconfirmationResult.AlreadyConfirmed:
                message = this.res("ReconfirmationResultConfirmed");
                break;
            case AccountReconfirmationResult.Complete:
                message = this.res("ReconfirmationResultComplete");
                break;
        }

        return (
            <MuiMessageBox
                variant="info"
                size="large"
                message={
                    <>
                        <Typography variant="body2" color="inherit">
                            {message}
                        </Typography>
                    </>
                }
            />
        );
    }

    ///
    private renderConfirmation() {

        if (this.state.confirmationResult == AccountConfirmationResult.Expired)
            return this.renderExpired();

        if (this.state.confirmationResult == AccountConfirmationResult.NotFound)
            return this.renderNotFound();

        let message = "";
        switch (this.state.confirmationResult) {
            case AccountConfirmationResult.AlreadyConfirmed:
                message = this.res("AlreadyConfirmedMessage");
                break;
            default:
                message = this.res("ConfirmedMessage");
                break;
        }

        return <MuiMessageBox
            variant="success"
            size="large"
            message={
                <Typography variant="h5" color="inherit">
                    <p>{message}</p>
                </Typography>
            }
            action={
                <MuiRegularButton
                    color="default"
                    size="large"
                    onClick={() => { this.props.history.replace(RoutePaths.Login); }}
                >
                    <span>{this.props.t("Views.Public.Login.SubmitText")}</span>
                </MuiRegularButton>
            }
        />;
    }

    ///
    public componentDidMount(): void {
        this.executeConfirmation();
    }

    ///
    public render() {
        const { confirmationResult, newConfirmationResult, error, submiting } = this.state;

        return (
            <>
                {submiting && <RequestIndicator type="waiting" />}

                {error != null && this.renderError()}

                {error == null && confirmationResult != null && this.renderConfirmation()}

                {error == null && newConfirmationResult != null && this.renderNewConfirmation()}
            </>
        );
    }
}

export default withTranslation()(withRouter(withSnackbar(withStyles(styles)(AccountConfirmation))));